/* eslint-disable no-nested-ternary */
import { PersonSidebarUpdates } from '@aibees/elephant/components/person-sidebar-updates';
import { SidebarPersonalityAnalysis } from '@aibees/elephant/components/sidebar-personality-analysis';
import { usePersonReadDataContext } from '@aibees/elephant/context';
import { Insight } from '@aibees/elephant/feature/search-drawer/feature/insight';
import { useSearchFiltersStore } from '@aibees/elephant/feature/search-filters/store';
import { SidebarLookalike } from '@aibees/elephant/feature/sidebar-lookalike';
import { TabDrawer } from '@aibees/elephant/feature/tab-drawer/tab-drawer';
import { Tab } from '@aibees/elephant/feature/tab-drawer/types';
import { useGetPersonUpdatesData } from '@aibees/elephant/hooks/people/use-get-person-updates-data';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useIsPeopleLookalikeEnabled } from '@aibees/elephant/hooks/people';
import { useBoolean } from '@aibees/shared/hooks';
import { useTableSearchItemStore } from '@aibees/shared/store/table-search-item';
import { Divider, Stack } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useSearchPersonStore } from '../../store';
import { HeaderNavigation } from './components/header-navigation';
import { PersonCard } from './components/person-card';
import { Overview } from './feature/overview';
import { usePeopleDrawerStore } from './store';
import { SearchProvider } from '@aibees/shared/context';
import { PeopleTimeZoneInfoSidebar } from './components/people-time-zone-info-sidebar';

export const PeopleDrawer = memo(() => {
  const state = useBoolean(false);
  const { id, setId } = useSearchPersonStore();
  const { api } = usePersonReadDataContext();
  const { defaultActiveTab } = usePeopleDrawerStore();
  const { setSelectedItem } = useTableSearchItemStore();

  const setRecordManagementOpen =
    useSearchFiltersStore.useSetRecordManagementOpen();
  const setViewAllLists = useSearchFiltersStore.useSetViewAllLists();
  const setViewAllLabels = useSearchFiltersStore.useSetViewAllLabels();
  const { hasAnyUpdates } = useGetPersonUpdatesData(api?.data);

  const isPeopleLookalikeEnabled = useIsPeopleLookalikeEnabled();

  useEffect(() => {
    setTabs((prev) =>
      prev.map((tab) => {
        if (tab.title === 'Update') {
          return { ...tab, dotBadge: hasAnyUpdates };
        }
        return tab;
      })
    );
  }, [hasAnyUpdates]);

  useEffect(() => {
    if (!state.value) {
      setViewAllLists(false);
      setViewAllLabels(false);
      setSelectedItem(undefined);
      setTabs((prev) =>
        prev.map((tab) => ({
          ...tab,
          active: tab.title === 'Overview',
        }))
      );
    } else {
      if (defaultActiveTab) {
        setTabs((prev) =>
          prev.map((tab) => ({
            ...tab,
            active: tab.title === defaultActiveTab,
          }))
        );
      }
    }
  }, [state.value, defaultActiveTab]);

  useEffect(() => {
    state.setValue(!!id);
    if (!id) {
      setRecordManagementOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChangeTabs = (tab: Tab) => {
    if (tab.disabled) return;
    setTabs((prev) => {
      return prev.map((t) =>
        t.title === tab.title ? { ...t, active: true } : { ...t, active: false }
      );
    });
  };

  function handleClickUpdate() {
    setTabs((prev) =>
      prev.map((tab) => ({
        ...tab,
        active: tab.title === 'Update',
      }))
    );
  }

  const [tabs, setTabs] = useState<Tab[]>([
    {
      title: 'Overview',
      icon: 'dashboard-outline',
      active: true,
      disabled: false,
      component: <Overview handleChangeTab={handleClickUpdate} />,
    },
    {
      title: 'Insight',
      description: '(Coming Soon)',
      icon: 'Light-bulb-outline',
      active: false,
      disabled: true,
      comingSoon: true,
      component: <Insight />,
    },
    {
      title: 'Lookalike',
      description: !isPeopleLookalikeEnabled ? '(Coming Soon)' : undefined,
      icon: 'fingerprint',
      active: false,
      disabled: !isPeopleLookalikeEnabled,
      component: <SidebarLookalike />,
    },
    {
      title: 'AI Personality',
      icon: 'person-search-outline',
      active: true,
      disabled: false,
      component: <SidebarPersonalityAnalysis />,
    },
    {
      title: 'Update',
      icon: 'arrows-reload',
      active: false,
      disabled: false,
      component: <PersonSidebarUpdates />,
      dotBadge: hasAnyUpdates,
    },
  ]);

  const activeTab = tabs.find((i) => i.active);

  return (
    <div
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <SearchProvider>
        <TabDrawer
          width={483}
          state={state}
          header={<HeaderNavigation drawerState={state} />}
          handleChangeTabs={handleChangeTabs}
          subHeader={
            activeTab?.title !== 'AI Personality' ? (
              <Stack>
                <Stack padding={1.5}>
                  <PersonCard />
                </Stack>
                <Divider dashed />
                <PeopleTimeZoneInfoSidebar />
                <Divider />
              </Stack>
            ) : undefined
          }
          tabs={tabs}
          tabContent={activeTab?.component ?? <div />}
          onClose={setId}
        />
      </SearchProvider>
    </div>
  );
});

PeopleDrawer.displayName = 'PeopleDrawer';
